import { fmt2 } from "@/lib/sqaafLabels";
import {
  buildBlockActionQueue,
  buildBlockComparisonRows,
  buildBlockHeroCopy,
  buildBlockInsightCards,
} from "@/utils/sqaafBlockDerived";
import {
  buildExecutiveActionSignal,
  buildExecutiveHeroBody,
  buildInsightCards,
  buildLowestQuestionsTable,
  buildPolicyActions,
  buildWatchlistRows,
  getDistrictBandCopy,
  getDistrictBandLabel,
} from "@/utils/sqaafExecutiveDerived";
import { buildPolicyInfoCard } from "@/utils/sqaafPolicyDerived";
import {
  buildSchoolActionQueue,
  buildSchoolHeroCopy,
  buildSchoolInsightCards,
} from "@/utils/sqaafSchoolDerived";
import {
  buildSectionHeroCopy,
  buildSectionInsightCards,
  mergeSectionRows,
} from "@/utils/sqaafSectionDerived";

const section = (title, lines = []) => {
  const body = lines.filter(Boolean).map((l) => (l.startsWith("-") ? l : `- ${l}`)).join("\n");
  return `## ${title}\n${body}`;
};

export function buildSqafExecutiveMarkdown({
  overview = {},
  blockPerf = {},
  intervention = {},
  lowestQuestions = [],
} = {}) {
  const criticalCount =
    overview?.band_counts?.Critical ?? intervention?.intervention_summary?.critical_count ?? 0;
  const districtBand = getDistrictBandLabel(overview?.district_mean ?? 0);

  return [
    section("Insights", [
      `${getDistrictBandCopy(districtBand)}: ${buildExecutiveHeroBody(overview)}`,
      ...buildInsightCards(overview, criticalCount).map((c) => `${c.title}: ${c.body}`),
    ]),
    section("Root Cause Signals", [
      buildExecutiveActionSignal(overview, blockPerf, intervention),
      ...buildWatchlistRows(overview, blockPerf).map((w) => `${w.block} (${fmt2(w.score)}%): ${w.note}`),
    ]),
    section("Recommendations", buildPolicyActions(overview, criticalCount, intervention).map((a) => `${a.title}: ${a.body}`)),
    section(
      "Priority Action Items",
      [
        ...buildPolicyActions(overview, criticalCount, intervention).map((a) => `${a.priority} — ${a.title}: ${a.body}`),
        ...buildLowestQuestionsTable(lowestQuestions).map((q) => `${q.theme} (${q.scorePct}%): ${q.action}`),
      ]
    ),
  ].join("\n\n");
}

export function buildSqafBlockMarkdown({ overview = {}, blockPerf = {} } = {}) {
  const blocks = blockPerf?.block_summary || [];
  const sorted = [...blocks].sort((a, b) => (b.mean ?? 0) - (a.mean ?? 0));
  const topBlocks = sorted.slice(0, 3);
  const bottomBlocks = [...blocks].sort((a, b) => (a.mean ?? 0) - (b.mean ?? 0)).slice(0, 5);
  const hero = buildBlockHeroCopy(overview, blocks);
  const districtMean = overview?.district_mean ?? 0;

  return [
    section("Insights", [
      `${hero.headline}: ${hero.body}`,
      ...buildBlockInsightCards(topBlocks, bottomBlocks, overview).map((c) => `${c.title}: ${c.body}`),
    ]),
    section("Root Cause Signals", [
      hero.actionNote,
      ...buildBlockComparisonRows(blocks, districtMean).map(
        (r) => `${r.block} rank ${r.rank} (${fmt2(r.mean)}%): ${r.note}`
      ),
    ]),
    section("Recommendations", buildBlockActionQueue(bottomBlocks, overview).map((a) => `${a.title}: ${a.body}`)),
    section(
      "Priority Action Items",
      buildBlockActionQueue(bottomBlocks, overview).map((a) => `${a.priority} — ${a.title}: ${a.body}`)
    ),
  ].join("\n\n");
}

export function buildSqafSectionMarkdown({ overview = {}, sectionAnalysis = {} } = {}) {
  const sections = mergeSectionRows(sectionAnalysis);
  const districtMean = sectionAnalysis?.district_mean ?? overview?.district_mean ?? 0;
  const hero = buildSectionHeroCopy(overview, sections, districtMean);

  return [
    section("Insights", [
      `${hero.headline}: ${hero.body}`,
      ...buildSectionInsightCards(sections, districtMean).map((c) => `${c.title}: ${c.body}`),
    ]),
    section("Root Cause Signals", [hero.actionNote]),
    section("Recommendations", buildSectionInsightCards(sections, districtMean).map((c) => `${c.title}: ${c.body}`)),
    section("Priority Action Items", [hero.actionNote]),
  ].join("\n\n");
}

export function buildSqafSchoolMarkdown({
  overview = {},
  school = {},
  schoolDetail = {},
} = {}) {
  const hero = buildSchoolHeroCopy(school, schoolDetail, overview);
  const insights = buildSchoolInsightCards(schoolDetail);
  const actions = buildSchoolActionQueue(school, schoolDetail);

  return [
    section("Insights", [
      `${hero.headline}: ${hero.body}`,
      hero.signal,
      ...insights.map((c) => `${c.title}: ${c.body}`),
    ]),
    section("Root Cause Signals", [
      hero.signal,
      ...(schoolDetail?.lowest_10_questions || [])
        .slice(0, 5)
        .map((q) => `Section ${q.section} (${fmt2(q.score_pct)}%): ${q.question}`),
    ]),
    section("Recommendations", actions.items.map((a) => `${a.title}: ${a.body}`)),
    section("Priority Action Items", actions.items.map((a) => `${a.priority} — ${a.title}: ${a.body}`)),
  ].join("\n\n");
}

export function buildSqafPolicyMarkdown({ overview = {}, intervention = {} } = {}) {
  const info = buildPolicyInfoCard(intervention, overview);
  const criticalCount =
    intervention?.intervention_summary?.critical_count ?? overview?.band_counts?.Critical ?? 0;
  const actions = buildPolicyActions(overview, criticalCount, intervention);
  const domains = (intervention?.priority_domains || [])
    .slice(0, 5)
    .map((d) => `Section ${d.section} (${fmt2(d.mean)}%)`);

  return [
    section("Insights", [`${info.title}: ${info.body}`]),
    section("Root Cause Signals", [
      ...domains,
      ...(intervention?.block_intervention_index || [])
        .slice(0, 3)
        .map((b) => `${b.block}: index ${fmt2(b.intervention_index)}, ${fmt2(b.critical_pct)}% critical`),
    ]),
    section("Recommendations", actions.map((a) => `${a.title}: ${a.body}`)),
    section("Priority Action Items", actions.map((a) => `${a.priority} — ${a.title}: ${a.body}`)),
  ].join("\n\n");
}
